import React, { useContext, useState, useEffect }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native'
import { Button, Text, Spinner, Toggle, Radio } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { updateMarketplaceSettings } from '../../global/Firebase/firebase'
import * as Sentry from "@sentry/react-native"
import { HintView } from '../../components/HintView'
import * as Clipboard from 'expo-clipboard'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { organizationAtom, storesDictAtom, productCategoriesListAtom, priceTypesAtom } from '../../global/AtomsProvider'

export default function MarketplaceSettingsScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const organization = useAtomValue(organizationAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const productCategoriesList = useAtomValue(productCategoriesListAtom)
  const priceTypes = useAtomValue(priceTypesAtom)
  const [context, setContext] = useContext(UserContext)
  const [marketplaceEnabled, setMarketplaceEnabled] = useState(organization?.marketplaceEnabled || false)
  const [disableNotInStockProducts, setDisableNotInStockProducts] = useState(organization?.disableNotInStockProducts || false)
  const [description, setDescription] = useState(organization?.description || '')
  const [linkEnabled, setLinkEnabled] = useState(organization?.linkEnabled || false)
  const [storeForMarketplace, setStoreForMarketplace] = useState(storesDict?.[organization?.storeForMarketplace] || Object.values(storesDict)[0])
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [enabledCategories, setEnabledCategories] = useState({})
  const [marketplacePriceTypeIds, setMarketplacePriceTypeIds] = useState(organization?.marketplacePriceTypeIds || [])

  // Sync storeForMarketplace when organization updates (e.g., after save via realtime)
  useEffect(() => {
    if (organization?.storeForMarketplace && storesDict) {
      const store = storesDict[organization.storeForMarketplace]
      if (store) {
        setStoreForMarketplace(store)
      }
    }
  }, [organization?.storeForMarketplace, storesDict])

  useEffect(() => {
    if (productCategoriesList) {
      const enabled = {}
      productCategoriesList.forEach(category => {
        enabled[category.id] = !category.marketplaceDisabled
      })
      setEnabledCategories(enabled)
    }
  }, [productCategoriesList])

  async function save() {
    setIsLoading(true)
    setSaveError('')
    const info = {
      marketplaceEnabled,
      description,
      linkEnabled,
      storeForMarketplace: storeForMarketplace.id,
      disableNotInStockProducts,
      marketplacePriceTypeIds,
    }
    try {
      await updateMarketplaceSettings(context.db, info, enabledCategories)      
      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        
        <Text category='label' style={styles.label}>
          {t('marketplaceSettings.linkName')}
        </Text>
        <Toggle 
          checked={linkEnabled}
          onChange={setLinkEnabled}
          style={styles.toggleField}
        >
          {t('marketplaceSettings.enabled')}
        </Toggle>
        {linkEnabled &&
          <HintView 
            text={t('marketplaceSettings.providedLink') + 'https://market.atlas-app.org/' + context.user.organizationId}
            iconName='alert-circle-outline' style={{marginVertical: 10}} 
            action={() => {
              if (Platform.OS === 'web') {
                const el = document.createElement('textarea')
                el.value = ('https://market.atlas-app.org/' + context.user.organizationId)
                document.body.appendChild(el)
                el.select()
                document.execCommand('copy')
                document.body.removeChild(el)
              } else {
                Clipboard.setString('https://market.atlas-app.org/' + context.user.organizationId)
              }
            }}
            callToAction={t('marketplaceSettings.copy')}
          />
        }
        <Text category='label' style={styles.label}>
          {t('marketplaceSettings.marketplaceName')}
        </Text>
        <Toggle 
          checked={marketplaceEnabled}
          onChange={(value) => {
            setMarketplaceEnabled(value)
            if (value) {
              setLinkEnabled(value)
            }
          }}
          style={styles.toggleField}
        >
          {t('marketplaceSettings.enabled')}
        </Toggle>
        <Text category='label' style={styles.label}>
          {t('marketplaceSettings.description')}
        </Text>
        <StyledTextInput 
          value={description}
          onChangeText={text => setDescription(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />
        <Text category='label' style={styles.label}>
          {t('marketplaceSettings.disableNotInStockProducts')}
        </Text>
        <Toggle 
          checked={disableNotInStockProducts}
          onChange={(value) => {
            setDisableNotInStockProducts(value)
          }}
          style={styles.toggleField}
        >
          {t('marketplaceSettings.enabled')}
        </Toggle>
        <Text category='label' style={styles.label}>{t('marketplaceSettings.storeForMarketplace')}</Text>
        { Object.values(storesDict).map((item, index) => (
          <Radio key={item.id} style={styles.radio} checked={storeForMarketplace?.id === item.id} onChange={() => {setStoreForMarketplace({id: Object.values(storesDict)[index].id, name: Object.values(storesDict)[index].name})}}>
            {item.name}
          </Radio>
        ))}     

        <HintView 
          text={t('marketplaceSettings.separateStoreForMarketplace')} 
          iconName='alert-circle-outline' style={{marginVertical: 10}} 
          action={() => navigation.navigate('UniversalList', {atom: storesDictAtom, searchField: 'name', detailScreen: 'StoreDetail', title: t('marketplaceSettings.stores')})}
          callToAction={t('marketplaceSettings.go')}
        />
        
        <Text category='label' style={styles.label}>
          {t('marketplaceSettings.enabledCategories')}
        </Text>
        {productCategoriesList && productCategoriesList.map((category) => (
          <Toggle 
            key={category.id}
            checked={enabledCategories[category.id]}
            onChange={(enabled) => {
              setEnabledCategories(prev => ({
                ...prev,
                [category.id]: enabled
              }))
            }}
            style={styles.toggleField}
          >
            {category.name}
          </Toggle>
        ))}

        {priceTypes && priceTypes.length > 0 && (
          <>
            <Text category='label' style={styles.label}>
              {t('marketplaceSettings.priceTypesForMarketplace')}
            </Text>
            {priceTypes.map((priceType) => (
              <Toggle
                key={priceType.id}
                checked={marketplacePriceTypeIds.includes(priceType.id)}
                onChange={(enabled) => {
                  setMarketplacePriceTypeIds(prev =>
                    enabled
                      ? (prev.includes(priceType.id) ? prev : [...prev, priceType.id])
                      : prev.filter(id => id !== priceType.id)
                  )
                }}
                style={styles.toggleField}
              >
                {priceType.name}
              </Toggle>
            ))}
          </>
        )}

        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}} />
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  toggleField: {
    alignSelf: 'flex-start',
    marginBottom: 5,
  },
  radio: {
    marginBottom: 5
  },
});